import React from 'react';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import { EmailIcon, PhoneIcon, AcademyIcon } from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import NavigationButton from '../ui/NavigationButton';
import Divider from '@mui/material/Divider';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/general/general-slice-selectors';
import { replaceArgsInMessage } from '../../utils/strings';
import { getCurrentPageName, trackEvent } from '../../services/analytics';
import {
  AnalyticsEvent,
  HelpLink,
  HelpLinkSource,
  SupportChannel,
} from '../../services/analytics-events';

const SupportDudePopover: React.FC<{ anchorEl: HTMLButtonElement | null; onClose: () => void }> = ({
  anchorEl,
  onClose,
}) => {
  const { t } = useTranslation();

  const currentUser = useAppSelector((state) => selectUser(state));

  // The support contact's address and number are never sent, only which channel was used.
  const trackSupportContact = (channel: SupportChannel) => {
    trackEvent(AnalyticsEvent.SupportContactUsed, {
      channel,
      source_page: getCurrentPageName(),
    });
  };

  const emailClickHandler = () => {
    if (currentUser.data.support.email != null) {
      trackSupportContact(SupportChannel.Email);
      const subject = t('email-contact-support-subject');
      const emailBody = t('email-contact-support-body-description');
      const mailUrl =
        'mailto:' + currentUser.data.support.email + '?subject=' + subject + '&body=' + emailBody;
      window.open(mailUrl, '_blank')?.focus();
    }
  };

  const phoneClickHandler = () => {
    if (currentUser.data.support.phoneNumber != null) {
      trackSupportContact(SupportChannel.Phone);
      window.open(`tel:${currentUser.data.support.phoneNumber}`);
    }
  };

  const hubspotClickHandler = () => {
    trackEvent(AnalyticsEvent.HelpLinkOpened, {
      link: HelpLink.KnowledgeHub,
      source: HelpLinkSource.SupportPopover,
      source_page: getCurrentPageName(),
    });
    window.open('https://knowledgehub.kemaro.ch/');
  };

  return (
    <React.Fragment>
      <Popover
        id="role-popover-id"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack>
          <Typography variant="h3" padding={2} pb={1}>
            {t('title-contact-support')}
          </Typography>
          <NavigationButton
            isNavOpen={true}
            title={t('info-kemaro-hubspot')}
            icon={<AcademyIcon />}
            onClick={hubspotClickHandler}
          />
          <Divider />
          <NavigationButton
            isNavOpen={true}
            title={currentUser.data.support.email ?? ''}
            icon={<EmailIcon />}
            onClick={emailClickHandler}
            lowerCase
          />
          <NavigationButton
            isNavOpen={true}
            title={currentUser.data.support.phoneNumber ?? ''}
            icon={<PhoneIcon />}
            onClick={phoneClickHandler}
          />
          <Typography variant="overline" p={2} pt={0} pb={0}>
            {replaceArgsInMessage(t('info-support-provided-by'), [currentUser.data.support.name])}
          </Typography>
        </Stack>
      </Popover>
    </React.Fragment>
  );
};

export default SupportDudePopover;
